import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {fromEvent, lastValueFrom, takeUntil} from 'rxjs';
import {ActionItem, AiService, Severity} from '../../../../../api';
import {BadgeTone, ElciaBadge} from '../../../../components/badge/badge';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {Loader} from '../../../../components/loader/loader';
import {Label} from '../../../../components/label/label';
import {QueryKey} from '../../../../constants/query-key';

@Component({
  selector: 'elcia-customer-analysis-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ElciaDialog, Loader, Label, TranslatePipe, ElciaBadge],
  templateUrl: './customer-analysis-dialog.html',
  styleUrl: './customer-analysis-dialog.scss',
})
export class CustomerAnalysisDialog {
  private readonly aiService = inject(AiService);

  readonly customerId = input.required<number>();

  readonly onClose = output();

  // Nothing is cached client side: the server serves the same analysis while the customer data has
  // not changed, so a fresh call here is not a fresh model call. No retry nor background refetch
  // either, a model call is slow and billed.
  // The abort signal closes the request when the dialog does: an analysis nobody reads is still
  // billed while it runs.
  private readonly analysisQuery = injectQuery(() => ({
    queryKey: [QueryKey.CUSTOMER_ANALYSIS, this.customerId()],
    queryFn: ({signal}: {signal: AbortSignal}) => lastValueFrom(
      this.aiService.getCustomerAnalysis(this.customerId()).pipe(takeUntil(fromEvent(signal, 'abort')))),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  }));

  protected readonly isLoading = computed(() => this.analysisQuery.isPending());
  protected readonly hasFailed = computed(() => this.analysisQuery.isError());

  protected readonly executiveSummary = computed(() => this.analysisQuery.data()?.analysis.summary.executiveSummary ?? '');
  protected readonly needs = computed<string[]>(() => this.analysisQuery.data()?.analysis.summary.needs ?? []);
  protected readonly risks = computed<string[]>(() => this.analysisQuery.data()?.analysis.summary.risks ?? []);
  protected readonly actions = computed<ActionItem[]>(() => this.analysisQuery.data()?.analysis.recommendation.actions ?? []);

  protected severityTone(severity: Severity): BadgeTone {
    switch (severity) {
      case Severity.High:
        return 'warning';
      case Severity.Medium:
        return 'info';
      default:
        return 'neutral';
    }
  }
}
